import { useQuery } from '@tanstack/react-query';
import { Download, BarChart3, AlertTriangle, Stethoscope, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

interface LineListing {
  caseNumber: string;
  sourceType: string;
  sourceChannel: string;
  receiptDate: string;
  reporterCountry: string | null;
  patientInitials: string | null;
  patientAgeYears: number | null;
  patientSex: string | null;
  suspectProduct: string;
  indication: string | null;
  doseInfo: string | null;
  adverseEventText: string;
  meddraPT: string | null;
  meddraPTCode: string | null;
  meddraSOC: string | null;
  seriousness: string | null;
  expectedness: string | null;
  causality: string | null;
  outcome: string | null;
  reportingDeadline: string | null;
  isExpedited: boolean;
  status: string;
  submittedAt: string | null;
  createdAt: string;
}

interface Summary {
  totalCases: number;
  expeditedCount: number;
  byProduct: Record<string, number>;
  bySeriousness: Record<string, number>;
  byOutcome: Record<string, number>;
  bySOC: Record<string, number>;
  byStatus: Record<string, number>;
  bySource: Record<string, number>;
}

const SERIOUSNESS_COLORS: Record<string, string> = {
  death: 'bg-red-600',
  life_threatening: 'bg-red-400',
  hospitalisation: 'bg-orange-400',
  medically_important: 'bg-amber-400',
  non_serious: 'bg-emerald-400',
};

const SERIOUSNESS_LABELS: Record<string, string> = {
  death: 'Death',
  life_threatening: 'Life-threatening',
  hospitalisation: 'Hospitalisation',
  medically_important: 'Medically important',
  non_serious: 'Non-serious',
};

function seriousnessVariant(s: string | null): 'destructive' | 'warning' | 'success' | 'secondary' {
  if (!s) return 'secondary';
  if (s === 'death' || s === 'life_threatening') return 'destructive';
  if (s === 'hospitalisation' || s === 'medically_important') return 'warning';
  return 'success';
}

export function PVReportingPage() {
  const summary = useQuery<Summary>({
    queryKey: ['pv-reporting-summary'],
    queryFn: async () => (await api.get('/api/pv/reporting/summary')).data,
  });

  const lineListing = useQuery<{ items: LineListing[]; total: number }>({
    queryKey: ['pv-reporting-line-listing'],
    queryFn: async () => (await api.get('/api/pv/reporting/line-listing')).data,
  });

  async function exportCsv() {
    const res = await api.get('/api/pv/reporting/line-listing?format=csv', { responseType: 'blob' });
    const blob = new Blob([res.data as BlobPart], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pv-line-listing.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const s = summary.data;
  const maxProduct = s ? Math.max(...Object.values(s.byProduct), 1) : 1;
  const maxSeriousness = s ? Math.max(...Object.values(s.bySeriousness), 1) : 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">PV Reporting</h1>
          <p className="text-sm text-muted-foreground">Aggregate safety data and line listings</p>
        </div>
        <Button onClick={exportCsv} variant="outline" size="sm">
          <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
        </Button>
      </div>

      {/* Summary cards */}
      {s && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Stethoscope className="h-4 w-4" /> Total cases
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{s.totalCases}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Clock className="h-4 w-4" /> Expedited (15-day)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{s.expeditedCount}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <BarChart3 className="h-4 w-4" /> Products tracked
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{Object.keys(s.byProduct).length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <AlertTriangle className="h-4 w-4" /> Serious cases
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">
                {s.totalCases - (s.bySeriousness['non_serious'] ?? 0)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Breakdown charts */}
      {s && (
        <div className="grid gap-6 md:grid-cols-2">
          {/* By seriousness */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">By seriousness</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(s.bySeriousness)
                .sort(([, a], [, b]) => b - a)
                .map(([key, count]) => (
                  <div key={key} className="flex items-center gap-3">
                    <div className="w-32 truncate text-xs text-muted-foreground">
                      {SERIOUSNESS_LABELS[key] ?? key}
                    </div>
                    <div className="flex-1">
                      <div
                        className={`h-5 rounded ${SERIOUSNESS_COLORS[key] ?? 'bg-gray-400'} transition-all`}
                        style={{ width: `${(count / maxSeriousness) * 100}%`, minWidth: '4px' }}
                      />
                    </div>
                    <div className="w-8 text-right text-xs font-medium">{count}</div>
                  </div>
                ))}
              {Object.keys(s.bySeriousness).length === 0 && (
                <div className="text-xs text-muted-foreground">No data yet</div>
              )}
            </CardContent>
          </Card>

          {/* By product */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">By suspect product</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(s.byProduct)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 10)
                .map(([product, count]) => (
                  <div key={product} className="flex items-center gap-3">
                    <div className="w-32 truncate text-xs text-muted-foreground" title={product}>
                      {product}
                    </div>
                    <div className="flex-1">
                      <div
                        className="h-5 rounded bg-primary/60 transition-all"
                        style={{ width: `${(count / maxProduct) * 100}%`, minWidth: '4px' }}
                      />
                    </div>
                    <div className="w-8 text-right text-xs font-medium">{count}</div>
                  </div>
                ))}
              {Object.keys(s.byProduct).length === 0 && (
                <div className="text-xs text-muted-foreground">No data yet</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Line listing table */}
      <Card>
        <CardHeader>
          <CardTitle>Line listing</CardTitle>
        </CardHeader>
        <CardContent>
          {lineListing.isLoading && (
            <div className="text-sm text-muted-foreground">Loading...</div>
          )}
          {lineListing.data && lineListing.data.items.length === 0 && (
            <div className="text-sm text-muted-foreground">No cases found.</div>
          )}
          {lineListing.data && lineListing.data.items.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case #</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>MedDRA PT</TableHead>
                    <TableHead>Seriousness</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expedited</TableHead>
                    <TableHead>Receipt date</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Patient</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineListing.data.items.map((c) => (
                    <TableRow key={c.caseNumber}>
                      <TableCell className="font-mono text-xs">{c.caseNumber}</TableCell>
                      <TableCell className="max-w-[160px] truncate text-xs" title={c.suspectProduct}>
                        {c.suspectProduct}
                      </TableCell>
                      <TableCell className="text-xs">{c.meddraPT ?? '--'}</TableCell>
                      <TableCell>
                        <Badge variant={seriousnessVariant(c.seriousness)} className="text-[10px]">
                          {c.seriousness ? (SERIOUSNESS_LABELS[c.seriousness] ?? c.seriousness) : '--'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={c.status === 'SUBMITTED' || c.status === 'CLOSED' ? 'success' : 'warning'} className="text-[10px]">
                          {c.status.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{c.isExpedited ? 'Yes' : 'No'}</TableCell>
                      <TableCell className="text-xs">{formatDate(c.receiptDate)}</TableCell>
                      <TableCell className="text-xs">{c.reporterCountry ?? '--'}</TableCell>
                      <TableCell className="text-xs">
                        {c.patientInitials ?? '--'}{c.patientAgeYears ? ` / ${c.patientAgeYears}y` : ''}{c.patientSex ? ` / ${c.patientSex}` : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
